/**
 * F18 — NIPE tool: submitWizardStep
 *
 * Triggers the form submit handler for the current wizard step. The
 * final step (provisioning kick-off) routes through the F3 pending-
 * writes tray so the user sees a structured preview before the real
 * Convex mutation + Graph writes fire.
 *
 * Per Round 1: NIPE CAN silently submit non-final steps if the user
 * previously authorized the flow. Final-step submit routes through the
 * tray for UX clarity (a preview "creating site at X with folders Y...")
 * rather than as a security boundary.
 */

import type { NipeTool } from './types';

export type OnboardWizardSubmittableStep =
  | 'tenant'
  | 'consent'
  | 'signin'
  | 'firm'
  | 'provisioning';

export interface SubmitWizardStepContext {
  /** Optional. Only present when the SPA has the F18 wizard mounted. */
  submitWizardStep?: (args: {
    step: OnboardWizardSubmittableStep;
  }) => Promise<
    | { kind: 'submitted'; nextStep: string }
    | { kind: 'queued-for-approval'; pendingWriteId: string }
    | { kind: 'error'; message: string }
  >;
}

const SUBMITTABLE_STEPS: OnboardWizardSubmittableStep[] = [
  'tenant',
  'consent',
  'signin',
  'firm',
  'provisioning',
];

export const submitWizardStepTool: NipeTool<SubmitWizardStepContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'submitWizardStep',
      description:
        'Submit the current wizard step on behalf of the user. The provisioning step (final) routes through the F3 pending-writes tray for explicit approval; earlier steps submit directly.',
      parameters: {
        type: 'object',
        properties: {
          step: {
            type: 'string',
            description:
              'Step to submit. One of tenant, consent, signin, firm, provisioning.',
          },
        },
        required: ['step'],
      },
    },
  },
  mutatesRemote: true,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.submitWizardStep) {
      return { kind: 'error' as const, message: 'Wizard submit is not available — the F18 wizard is not currently mounted.' };
    }
    const step =
      typeof args.step === 'string' &&
      (SUBMITTABLE_STEPS as string[]).includes(args.step)
        ? (args.step as OnboardWizardSubmittableStep)
        : null;
    if (!step) {
      return {
        kind: 'error' as const,
        message: `Unknown step. Allowed: ${SUBMITTABLE_STEPS.join(', ')}`,
      };
    }
    return await ctx.submitWizardStep({ step });
  },
};
